import { HStack, Icon, Spacer, Text } from '@chakra-ui/react'
import { useMemo } from 'react'
import { IoIosImages } from 'react-icons/io'
import {
  Order_By,
  useGetOrganizationsQuery,
  useUpdateLastOrgContextMutation,
} from '../../generated/graphql'
import {
  getOrgData,
  useIsEmployeeView,
  useOrgID,
  useUserID,
} from '../../hooks/id_token_claims'
import {
  DOPPEL_CLOUD_BLUE,
  DOPPEL_ENCRYPT_GREEN_SHADE,
  DOPPEL_FIREWALL_ORANGE,
  DOPPEL_SECURE,
  DOPPEL_INTERNAL_PURPLE,
  DOPPEL_WHITE,
  DOPPEL_TEXT_BLACK,
} from '../../utils/style'
import { ALL_MODULES_LABEL, ALL_MODULES_PATH } from '../../utils/constants'
import { DoppelTag } from '../shared/doppel_tag'
import { getTabsForOrg, useSelectedReportType } from '@/pages/reports'
import { useRouter } from 'next/router'
import { OrganizationStatus, OrganizationStatusLabels } from '@/generated/enums'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useAtomValue, useSetAtom } from 'jotai'
import { userProductsAtom } from '@/atoms/permissions'
import { organizationIdAtom } from '@/atoms/organizations'
import PaginatedDropdown from '../doppel_design/paginated_dropdown/paginated_dropdown'
import { organizationSwitchRedirect } from '@/utils/organization'
import { useOrgEnabledProducts } from '@/hooks/org_enabled_products'

export default function OrgPicker() {
  const orgId = useOrgID()
  const [userId] = useUserID()
  const router = useRouter()

  const setOrgId = useSetAtom(organizationIdAtom)
  const [orgData] = getOrgData()
  const userProducts = useAtomValue(userProductsAtom)
  const isRbacEnabled = useFeatureFlagEnabled('rbac')

  const { data } = useGetOrganizationsQuery({
    variables: {
      orderBy: [{ name: Order_By.Asc }],
    },
  })
  const [isEmployeeView] = useIsEmployeeView()
  const [updateLastOrgContextMutation, { data: _ }] = useUpdateLastOrgContextMutation()
  const [, handleReportTypeChange] = useSelectedReportType()
  const { products: enabledOrgProducts } = useOrgEnabledProducts(orgData?.id)

  const organizations = useMemo(() => {
    const orgs = data?.organizations || []
    const sortedOrgs = [...orgs].sort((a, b) => orgStatusRank(b) - orgStatusRank(a))
    return sortedOrgs
  }, [data])

  const getFilteredOrgs = (searchQuery: string) => {
    return organizations.filter((org) => {
      const orgStatus = OrganizationStatusLabels[org.organization_status]
      const searchQueryLower = searchQuery.toLowerCase()
      return (
        org.name.toLowerCase().includes(searchQueryLower) ||
        orgStatus.toLowerCase() === searchQueryLower
      )
    })
  }

  if (!isEmployeeView) {
    return null
  }

  return (
    <HStack>
      <PaginatedDropdown
        autoTriggerSearch={true}
        isInternal={true}
        itemToKey={(org) => org.id}
        itemToLabel={(org) => (
          <HStack width="100%">
            <Text>{org.name}</Text>

            {org.organization_type == 'marketplace' && (
              <Icon as={IoIosImages} h={5} pl="1" w={5} />
            )}

            <Spacer />

            <DoppelTag
              bgColor={getColorForOrgStatus(org.organization_status)}
              color={DOPPEL_WHITE}
              loading={false}
              tag={OrganizationStatusLabels[org.organization_status]}
            />
          </HStack>
        )}
        menuLabel={orgData.name}
        onSelect={async (org) => {
          if (!org) return
          await updateLastOrgContextMutation({
            variables: {
              last_org_context: org.id,
              id: userId,
            },
            refetchQueries: ['GetLastOrgContext'],
            awaitRefetchQueries: true,
          })
          setOrgId(org.id)
          const tabs = [
            {
              name: ALL_MODULES_LABEL,
              route: `/${ALL_MODULES_PATH}`,
              isVisible: true,
            },
            ...getTabsForOrg(org, enabledOrgProducts, userProducts, isRbacEnabled),
          ]
          const currentTabRoute = router.asPath
          const selectedTab = tabs.find(
            (tab) => tab.route === currentTabRoute && tab.isVisible,
          )
          const firstVisibleTab = selectedTab || tabs.find((tab) => tab.isVisible)
          if (firstVisibleTab) {
            handleReportTypeChange(firstVisibleTab.name)
          }
          const currentPath = router.asPath.split('/').slice(1).join('/')
          const redirectPath = organizationSwitchRedirect(
            currentPath,
            orgData.is_parent,
            org.is_parent,
          )
          await router.push(redirectPath)
        }}
        searchPlaceholderText="Search orgs..."
        selectedItem={organizations.find((org) => org.id === orgId)}
        textColor={DOPPEL_TEXT_BLACK}
        useFetchCountQuery={(searchQuery) => {
          return {
            count: getFilteredOrgs(searchQuery).length,
            loading: false,
            refetch: () => {},
          }
        }}
        useFetchDataQuery={({ searchQuery, limit, offset }) => {
          const filtered = getFilteredOrgs(searchQuery)
          return {
            items: filtered.slice(offset, offset + limit),
            loading: false,
            refetch: () => {},
          }
        }}
      />
    </HStack>
  )
}

function orgStatusRank(org) {
  switch (org.organization_status) {
    case OrganizationStatus.ACTIVE:
      return 5
    case OrganizationStatus.EVALUATING:
      return 4
    case OrganizationStatus.PITCHING:
      return 3
    case OrganizationStatus.TRIAL_ENDED:
      return 2
    case OrganizationStatus.INACTIVE:
      return 1
    case OrganizationStatus.TEST_ORG:
      return 0
  }
}

export function getColorForOrgStatus(orgStatus) {
  switch (orgStatus) {
    case OrganizationStatus.ACTIVE:
      return DOPPEL_ENCRYPT_GREEN_SHADE
    case OrganizationStatus.TRIAL_ENDED:
      return DOPPEL_SECURE
    case OrganizationStatus.EVALUATING:
      return DOPPEL_FIREWALL_ORANGE
    case OrganizationStatus.TEST_ORG:
      return DOPPEL_INTERNAL_PURPLE
    case OrganizationStatus.PITCHING:
      return DOPPEL_CLOUD_BLUE
    case OrganizationStatus.INACTIVE:
      return DOPPEL_SECURE
  }
}
